import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginEmail, loginGoogle, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(location.state?.error || '');

  useEffect(() => {
    if (user) {
      navigate(user.email_verified === false ? '/check-email' : '/app', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const u = await loginEmail(email, password);
      navigate(u.email_verified === false ? '/check-email' : '/app', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <video
          className="auth-video"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/hero_video.mp4" type="video/mp4" />
        </video>
        <div className="auth-video-overlay"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 44, height: 44 }}
              />
              <span className="logo-text">Ezai Build</span>
            </Link>
            <h1 className="display-medium auth-title">Welcome Back</h1>
            <p className="body-medium auth-subtitle">
              Sign in to continue creating stunning 3D visualizations
            </p>
          </div>

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label className="form-label body-medium">Email</label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  className="form-input"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label body-medium">Password</label>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-footer">
              <label className="checkbox-label body-small">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="body-small forgot-link" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="auth-error body-small" data-testid="auth-error">{error}</p>
            )}

            <Button
              type="submit"
              className="btn-primary btn-large auth-btn"
              disabled={submitting}
              data-testid="login-submit-btn"
            >
              {submitting ? <Loader2 size={20} className="spinner" /> : 'Sign In'}
            </Button>
          </form>

          <div className="auth-divider">
            <span className="body-small">or continue with</span>
          </div>

          <div className="social-buttons">
            <button
              type="button"
              className="social-btn"
              onClick={loginGoogle}
              data-testid="google-login-btn"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" />
              <span className="body-medium">Google</span>
            </button>
          </div>

          <p className="auth-switch body-medium">
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Sign up
            </Link>
          </p>
        </div>

        <div className="auth-info">
          <div className="info-card">
            <h3 className="heading-2">Transform Your Designs</h3>
            <p className="body-medium">
              Upload 2D architectural drawings and watch AI create stunning 3D visualizations in seconds.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
